import type { LocalDate } from "@js-joda/core";
import { CompanyEmploymentTaxType } from "model/Salary";
import type { UpdateWorkerTaxV3Request } from "model/Salary";
import { Api } from "services/api/api.service";
import type {
  AddStaffWorkerTaxRequest,
  StaffProfileData,
} from "services/api/company/staff.service";
import {
  toIsoDateStartOfDayUtc,
  toNonNegativeNumber,
} from "../domains/employment/profilePanel.payload.logic";
import type { EmploymentDetailsForm } from "../profilePanel.contract";

export interface DraftTaxItem {
  readonly id: number;
  readonly startDate: string;
  readonly value: number | null;
}

interface AddTaxDraftItemParams {
  currentEmploymentStartDate: LocalDate | null;
  editedEmploymentDetails: EmploymentDetailsForm;
  effectiveFromDate: LocalDate | null;
  nextDraftId: number;
  setSaveError: (value: string) => void;
  translate: (key: string, options?: Record<string, unknown>) => string;
}

interface DeleteTaxDraftItemParams {
  taxDraftHistory: DraftTaxItem[];
  taxId: number;
}

interface HasTaxDraftHistoryChangesParams {
  taxDraftHistory: DraftTaxItem[];
  taxInitialHistory: DraftTaxItem[];
}

interface SaveTaxDraftHistoryParams {
  companyId: number | null;
  executeSavingAction: (action: () => Promise<void>) => void;
  isSaving: boolean;
  onWorkerUpdated?: () => void;
  profile: StaffProfileData | null;
  reloadProfile: () => void;
  taxDraftHistory: DraftTaxItem[];
  taxInitialHistory: DraftTaxItem[];
  closeModal: () => void;
  setSaveError: (value: string) => void;
  translate: (key: string, options?: Record<string, unknown>) => string;
  workerId: number | null;
}

interface UpdateTaxDraftValueParams {
  readonly taxDraftHistory: DraftTaxItem[];
  readonly taxId: number;
  readonly value: number;
}

export function toTaxDraftHistory(
  profile: StaffProfileData | null,
): DraftTaxItem[] {
  return (
    profile?.taxes.map((tax) => ({
      id: tax.id,
      startDate: tax.startDate ?? "",
      value: tax.value,
    })) ?? []
  );
}

export function addTaxDraftItem(
  params: AddTaxDraftItemParams,
): DraftTaxItem | null {
  if (params.effectiveFromDate == null) {
    params.setSaveError(params.translate("Alerts.SomethingWentWrong"));
    return null;
  }

  if (
    params.currentEmploymentStartDate != null &&
    params.effectiveFromDate.isBefore(params.currentEmploymentStartDate)
  ) {
    params.setSaveError(
      params.translate("Staff.ProfilePanelTaxDateBeforeEmploymentStart"),
    );
    return null;
  }

  if (params.effectiveFromDate.dayOfMonth() !== 1) {
    params.setSaveError(
      params.translate("Staff.ProfilePanelTaxDateMustBeFirstOfMonth"),
    );
    return null;
  }

  return {
    id: params.nextDraftId,
    startDate: toIsoDateStartOfDayUtc(params.effectiveFromDate),
    value: toNonNegativeNumber(params.editedEmploymentDetails.taxTable),
  };
}

export function deleteTaxDraftItem(
  params: DeleteTaxDraftItemParams,
): DraftTaxItem[] {
  return params.taxDraftHistory.filter((tax) => tax.id !== params.taxId);
}

export function updateTaxDraftValue(
  params: UpdateTaxDraftValueParams,
): DraftTaxItem[] {
  return params.taxDraftHistory.map((tax) =>
    tax.id === params.taxId ? { ...tax, value: params.value } : tax,
  );
}

export function hasTaxDraftHistoryChanges(
  params: HasTaxDraftHistoryChangesParams,
): boolean {
  return (
    JSON.stringify(params.taxDraftHistory) !==
    JSON.stringify(params.taxInitialHistory)
  );
}

export function saveTaxDraftHistory(params: SaveTaxDraftHistoryParams): void {
  if (params.isSaving) return;
  if (params.companyId == null || params.workerId == null) {
    params.setSaveError(params.translate("Alerts.SomethingWentWrong"));
    return;
  }

  const initialExistingIds = new Set(
    params.taxInitialHistory.filter((tax) => tax.id > 0).map((tax) => tax.id),
  );
  const draftExistingIds = new Set(
    params.taxDraftHistory.filter((tax) => tax.id > 0).map((tax) => tax.id),
  );
  const deletedIds = Array.from(initialExistingIds).filter(
    (id) => !draftExistingIds.has(id),
  );
  const addedItems = params.taxDraftHistory.filter((tax) => tax.id < 0);
  const modifiedItems = params.taxDraftHistory.filter((tax) => {
    if (tax.id <= 0) return false;
    const initialTax = params.taxInitialHistory.find(
      (initialItem) => initialItem.id === tax.id,
    );
    return initialTax != null && initialTax.value !== tax.value;
  });
  const hasChanges =
    deletedIds.length > 0 || addedItems.length > 0 || modifiedItems.length > 0;

  if (!hasChanges) {
    params.setSaveError("");
    params.closeModal();
    return;
  }

  const companyId = params.companyId;
  const workerId = params.workerId;
  const activeTax =
    params.profile?.taxes.find((tax) => tax.isActive) ??
    params.profile?.taxes[0] ??
    null;

  params.executeSavingAction(async () => {
    for (const taxId of deletedIds) {
      await Api().company.staff.deleteWorkerTax(companyId, workerId, taxId);
    }

    for (const tax of addedItems) {
      const payload: AddStaffWorkerTaxRequest = {
        startDate: tax.startDate,
        taxType: activeTax?.type ?? "Undefined",
        value: tax.value ?? 0,
      };
      await Api().company.staff.addWorkerTax(companyId, workerId, payload);
    }

    for (const tax of modifiedItems) {
      const existingTax = params.profile?.taxes.find(
        (profileTax) => profileTax.id === tax.id,
      );
      const payload: UpdateWorkerTaxV3Request = {
        startDate: tax.startDate,
        taxType: toCompanyEmploymentTaxType(
          existingTax?.type ?? activeTax?.type,
        ),
        value: tax.value ?? 0,
      };
      await Api().company.staff.updateWorkerTax({
        companyId,
        data: payload,
        taxId: tax.id,
        workerId,
      });
    }

    params.setSaveError("");
    params.closeModal();
    params.reloadProfile();
    params.onWorkerUpdated?.();
  });
}

function toCompanyEmploymentTaxType(
  value: string | null | undefined,
): CompanyEmploymentTaxType {
  if (value === CompanyEmploymentTaxType.TABLE) {
    return CompanyEmploymentTaxType.TABLE;
  }
  if (value === CompanyEmploymentTaxType.PERCENTAGE) {
    return CompanyEmploymentTaxType.PERCENTAGE;
  }
  return CompanyEmploymentTaxType.UNDEFINED;
}
